import { useEffect, useState } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { IDS, LOGO_URL } from '@/constants/testIds';

const NAV = [
  { id: 'home', label: 'Home', testId: IDS.header.navHome },
  { id: 'about', label: 'About', testId: IDS.header.navAbout },
  { id: 'services', label: 'Services', testId: IDS.header.navServices },
  { id: 'contact', label: 'Contact', testId: IDS.header.navContact },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-md border-b border-navy-100 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <button
          data-testid={IDS.header.logo}
          onClick={() => scrollTo('home')}
          className="flex items-center gap-3 group"
          aria-label="S.K. Infrastructure Home"
        >
          <div className="h-12 w-12 rounded-sm overflow-hidden bg-white ring-1 ring-navy-100 flex items-center justify-center">
            <img src={LOGO_URL} alt="S.K. Infrastructure" className="h-11 w-11 object-contain" />
          </div>
          <div className="hidden sm:block leading-tight text-left">
            <div className={`font-serif text-xl tracking-tight ${scrolled ? 'text-navy-800' : 'text-white'}`}>
              S.K. <span className="text-gold-400">Infrastructure</span>
            </div>
            <div className={`text-[10px] tracking-[0.2em] uppercase ${scrolled ? 'text-navy-500' : 'text-gold-300'}`}>
              Government Civil Works
            </div>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={n.testId}
              onClick={() => scrollTo(n.id)}
              className={`text-sm tracking-wide font-medium transition-colors ${
                scrolled ? 'text-navy-700 hover:text-gold-500' : 'text-white/90 hover:text-gold-300'
              }`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            data-testid={IDS.header.ctaContact}
            onClick={() => scrollTo('contact')}
            className="inline-flex items-center gap-2 bg-navy-700 hover:bg-navy-900 text-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold border border-transparent hover:border-gold-400 transition-all"
          >
            <Phone className="w-4 h-4 text-gold-400" />
            Get in Touch
          </button>
        </div>

        <button
          data-testid={IDS.header.mobileToggle}
          className={`lg:hidden p-2 ${scrolled ? 'text-navy-800' : 'text-white'}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-navy-100 shadow-lg">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`${n.testId}-mobile`}
                onClick={() => { setOpen(false); scrollTo(n.id); }}
                className="text-left text-navy-800 font-medium py-2 border-b border-navy-100"
              >
                {n.label}
              </button>
            ))}
            <button
              data-testid={`${IDS.header.ctaContact}-mobile`}
              onClick={() => { setOpen(false); scrollTo('contact'); }}
              className="bg-navy-700 text-white px-6 py-3 mt-2 text-xs tracking-[0.2em] uppercase font-bold border border-gold-400"
            >
              Get in Touch
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
